import CloseIcon from "@mui/icons-material/Close";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import {
  alpha,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { format, formatDistanceToNow } from "date-fns";
import { getUserColor } from "encamp-shared/src/utils/colors";
import { CommentThreadFragment } from "generated-graphql/graphql";
import { useCreateSubmissionComment, useDeleteSubmissionComment } from "hooks/comments";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFocusWithin } from "hooks/useFocusWithin";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getCommentAuthor, isCommentDeletableByUser } from "../Comments/commentPolicy";
import { useNotifyImmediatelyComment } from "./useNotifyImmediatelyComment";

const REGULATOR_DISCLAIMER =
  "These comments will not be seen by regulators and do not get submitted with your report";

/**
 * Whether a comment gets the "new since you last looked" highlight. Your own
 * comments are read by definition (you wrote them — they'd otherwise highlight
 * themselves the moment you add one with the panel open). `highlightAfter`
 * `undefined` disables highlighting; `null` means never-viewed (highlight all
 * of everyone else's).
 */
export function isUnreadComment({
  comment,
  highlightAfter,
  currentUserId,
}: {
  comment: Pick<CommentThreadFragment, "createdAt"> & {
    user?: { id: string } | null;
  };
  highlightAfter: string | null | undefined;
  currentUserId: string | undefined;
}): boolean {
  if (highlightAfter === undefined) return false;
  if (currentUserId && comment.user?.id === currentUserId) return false;
  if (highlightAfter === null) return true;
  return new Date(comment.createdAt) > new Date(highlightAfter);
}

interface SubmissionCommentsPanelProps {
  submissionId: string;
  comments: CommentThreadFragment[];
  /**
   * Comments created after this timestamp get the "new since you last looked"
   * highlight. `null` highlights everything (first-ever view); `undefined`
   * disables highlighting.
   */
  highlightAfter?: string | null;
  onClose: () => void;
  commentAdded?: (comment: CommentThreadFragment) => void;
  commentDeleted?: (commentId: string) => void;
}

/**
 * Right-rail comments panel for the waste submission stepper, per the
 * Chemicals-specs Figma comments design: a "Comments" header with a
 * not-seen-by-regulators info tooltip and a close button, the thread oldest →
 * newest (most recent at the bottom) where each comment carries its author's
 * color as a left border and unread comments are highlighted, and a single
 * "Comment..." input with an inline Submit button.
 */
export function SubmissionCommentsPanel({
  submissionId,
  comments,
  highlightAfter,
  onClose,
  commentAdded,
  commentDeleted,
}: SubmissionCommentsPanelProps) {
  const theme = useTheme();
  const { user: currentUser } = useCurrentUser();
  const { addComment, loading: addLoading } = useCreateSubmissionComment();
  const { deleteComment, loading: deleteLoading } = useDeleteSubmissionComment();
  const [draft, setDraft] = useState("");
  const { focused: inputFocused, focusProps } = useFocusWithin();
  const threadRef = useRef<HTMLDivElement>(null);

  const handleSave = useCallback(
    async (message: string, notifyImmediately: boolean) => {
      const result = await addComment(submissionId, message, notifyImmediately);
      const newComment = result?.data?.upsertComment;
      if (newComment) {
        commentAdded?.(newComment);
      }
    },
    [addComment, submissionId, commentAdded]
  );

  const {
    checkboxNode,
    dialogNode,
    handleSubmit: submitWithNotify,
  } = useNotifyImmediatelyComment({ onSave: handleSave });

  // Empty + unfocused shows just the textbox and placeholder; the Submit
  // button and checkbox appear once the user clicks in or has a draft.
  const showInputControls = inputFocused || draft.trim().length > 0 || addLoading;

  const sortedComments = useMemo(
    () =>
      [...comments].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ),
    [comments]
  );

  // Most recent comments sit at the bottom, so land the scroll there.
  useEffect(() => {
    const el = threadRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [sortedComments.length]);

  const handleSubmit = async () => {
    const message = draft.trim();
    if (!message || addLoading) return;
    const { clear } = await submitWithNotify(message);
    if (clear) {
      setDraft("");
    }
  };

  const handleDelete = async (commentId: string) => {
    commentDeleted?.(commentId);
    await deleteComment(commentId);
  };

  const isUnread = (comment: CommentThreadFragment) =>
    isUnreadComment({ comment, highlightAfter, currentUserId: currentUser?.id });

  return (
    <Stack sx={{ height: "100%", px: 3, pt: 2, pb: 3, gap: 2, bgcolor: "background.paper" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="h5">Comments</Typography>
        <Tooltip title={REGULATOR_DISCLAIMER}>
          <IconButton size="small" aria-label="About comments">
            <InfoOutlined sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <IconButton aria-label="Close comments" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        ref={threadRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          overflowWrap: "anywhere",
        }}
      >
        {sortedComments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            No comments yet.
          </Typography>
        ) : (
          <Stack sx={{ gap: 1 }}>
            {sortedComments.map((comment, index) => {
              const canDelete = isCommentDeletableByUser(comment, currentUser?.id);
              return (
                <Fragment key={comment.id}>
                  {index > 0 && <Divider />}
                  <Box
                    sx={{
                      borderLeft: `2px solid ${getUserColor(comment.user?.email ?? "")}`,
                      px: 2,
                      py: 0.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.5,
                      backgroundColor: isUnread(comment)
                        ? alpha(theme.palette.primary.main, 0.08)
                        : undefined,
                      "&:hover .comment-delete": { opacity: 1 },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        noWrap
                        sx={{ flex: 1, minWidth: 0 }}
                      >
                        {getCommentAuthor(comment)}
                      </Typography>
                      {canDelete && (
                        <IconButton
                          className="comment-delete"
                          size="small"
                          aria-label="Delete comment"
                          onClick={() => handleDelete(comment.id)}
                          disabled={deleteLoading}
                          sx={{ opacity: 0, transition: "opacity 0.2s", p: 0.25 }}
                        >
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                    <Tooltip
                      placement="top"
                      title={format(new Date(comment.createdAt), "M/d/yyyy 'at' h:mmaaa")}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ alignSelf: "flex-start" }}
                      >
                        {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                      </Typography>
                    </Tooltip>
                    <Typography
                      variant="body2"
                      color={comment.isSystemMessage ? "text.secondary" : "text.primary"}
                      fontStyle={comment.isSystemMessage ? "italic" : "normal"}
                      sx={{ whiteSpace: "pre-wrap" }}
                    >
                      {comment.comment}
                    </Typography>
                  </Box>
                </Fragment>
              );
            })}
          </Stack>
        )}
      </Box>

      {/* focusProps tracks focus-within so clicking the checkbox doesn't
          collapse the controls before the click lands. */}
      <Box {...focusProps}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Comment..."
          value={draft}
          disabled={addLoading}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Cmd/Ctrl+Enter submits. Plain Enter inserts a newline.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              void handleSubmit();
            }
          }}
          slotProps={{
            input: {
              endAdornment: showInputControls ? (
                <Button
                  variant="contained"
                  size="small"
                  disabled={!draft.trim() || addLoading}
                  onClick={handleSubmit}
                  sx={{ flexShrink: 0, alignSelf: "flex-end" }}
                >
                  Submit
                </Button>
              ) : undefined,
            },
          }}
        />
        {showInputControls && (
          <Box sx={{ mt: 0.5, display: "flex", justifyContent: "flex-end" }}>{checkboxNode}</Box>
        )}
      </Box>
      {dialogNode}
    </Stack>
  );
}
